import { useMemo } from 'react';
import { useGetLocationNpis, useGetHouseholdData } from '@frontend/api-insurance-detail';
import { PersonHelpers } from '@frontend/api-person';
import { useTranslation } from '@frontend/i18n';
import { useInsuranceContext } from '../providers/insurance-provider';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { usePersonInfoStore } from '../providers/person-info-provider';
import { OTHER_SUBSCRIBER_SELECTION_VALUE } from '../utils/constants';
import { DropdownOption } from '../utils/types';
import { getPayerCombinedId } from '../utils/utils';

interface UseGetFormDataReturnType {
  payerList: DropdownOption[];
  providerList: { nationalProviderId: string; taxId: string }[];
  nationalProviderIdList: string[];
  primSubscriberList: DropdownOption[];
  isLoading?: boolean;
}

export const useGetFormData = () => {
  const { t } = useTranslation('insuranceDetails');
  const { personInfo, locationId } = usePersonInfoStore();
  const { insuranceType } = useInsuranceType();
  const { isLoading: insuranceDetailsLoading, payersList: payerListQuery, insuranceDetails } = useInsuranceContext();
  const personId = personInfo?.PersonID as string;
  const householdId = personInfo?.WeaveHouseholdID as string;
  const providerListQuery = useGetLocationNpis(locationId!, {
    enabled: !!locationId,
  });
  const personHouseholdsQuery = useGetHouseholdData(locationId!, householdId, insuranceType);

  const isLoading = providerListQuery.isLoading || personHouseholdsQuery.isLoading || insuranceDetailsLoading;

  const { payerList, providerList, nationalProviderIdList, primSubscriberList } =
    useMemo<UseGetFormDataReturnType>(() => {
      if (isLoading) {
        return { payerList: [], providerList: [], nationalProviderIdList: [], primSubscriberList: [] };
      }

      const payerList =
        payerListQuery?.flatMap<DropdownOption>((payer) => {
          const primaryOption: DropdownOption = {
            label: payer?.name,
            value: payer?.id,
            uniqueCombinedId: getPayerCombinedId(payer?.id, payer?.name),
            alternativePayerIds: payer?.alternatePayerIds || [],
          };

          const alternateNames = (payer?.alternatePayerNames || [])?.filter(
            (altName) => altName && altName !== payer?.name
          );

          const alternativeOptions: DropdownOption[] = alternateNames?.map((altName) => ({
            label: altName,
            value: payer?.id,
            uniqueCombinedId: getPayerCombinedId(payer?.id, altName),
            alternativePayerIds: payer?.alternatePayerIds || [],
          }));

          return [primaryOption, ...alternativeOptions];
        }) ?? [];
      const providerList = providerListQuery.data ?? [];
      const nationalProviderIdList = providerList.reduce<string[]>((acc, provider) => {
        return provider.nationalProviderId ? [...acc, provider.nationalProviderId] : acc;
      }, []);
      const personHouseHoldList =
        personHouseholdsQuery.data?.persons?.sort((a, b) => {
          return new Date(a.birthdate || '').getTime() - new Date(b.birthdate || '').getTime();
        }) ?? [];

      const primSubscriberList = personHouseHoldList
        .filter((p) => p.personId !== personId)
        .map<DropdownOption>((person) => ({
          label: PersonHelpers.getFullName({
            FirstName: person.firstName,
            LastName: person.lastName,
            PreferredName: person.preferredName,
          }),
          value: person.personId,
          isGuardian: person.isGuardian,
        }));

      if (primSubscriberList.length > 0) {
        primSubscriberList.push({
          label: t('Other Subscriber'),
          value: OTHER_SUBSCRIBER_SELECTION_VALUE,
        });
      }

      return { payerList, providerList, nationalProviderIdList, primSubscriberList };
    }, [isLoading, payerListQuery, providerListQuery.data, personHouseholdsQuery.data?.persons, personId, t]);

  return {
    isLoading,
    payerList,
    providerList,
    nationalProviderIdList,
    primSubscriberList,
    insuranceDetails,
  };
};
